import {
  normalizeAddress,
  colorIndexFor,
  initialsFor,
  MAX_RECIPIENTS,
  type Participant,
} from "@catmail/shared";
import { X } from "lucide-react";
import { useMemo, useState, type KeyboardEvent } from "react";

import { Avatar, Button, Dialog } from "@/components";
import { FOCUS, FOCUS_ON_DARK, PRESS_FLAT } from "@/lib/interaction";

/** 아주 느슨한 검사. 정확한 판정은 서버가 한다. */
const EMAIL_SHAPE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export type NewChatDraft = {
  to: string[];
};

type NewChatDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** 최근 대화한 사람 목록 */
  contacts: Participant[];
  pending?: boolean;
  onStart: (draft: NewChatDraft) => void;
};

/**
 * 시안 06 · 새 채팅 시작
 *
 * **사람을 고르면 끝이다.** 방이 열리고, 할 말은 대화창에서 쓴다.
 *
 * 예전에는 여기가 두 걸음이었다 — 고르고, 무엇을 보낼지 쓰고. 방이 메일이 오갈
 * 때만 생기는 구조라 말을 걸려면 먼저 무슨 말을 할지 정해야 했다. 카카오톡도
 * 디스코드도 그러지 않는다. 이제 `POST /api/rooms`가 방을 먼저 열어준다.
 *
 * 그 앞의 사연도 남겨둔다. 아주 예전에는 사람을 고르는 순간 "안녕하세요"를
 * **실제로 발송했다.** 말풍선 하나가 곧 메일 한 통이라 상대 메일함에 인사만
 * 덜렁 든 메일이 남았다. 지금 방식은 그것과 다르다 — **메일은 한 통도 나가지
 * 않는다.** 상대는 첫 메일이 가야 이 대화를 안다.
 *
 * 참조는 여기서 받지 않는다. 방은 "같이 있는 사람들"로 정해지므로 참조도 결국
 * 같은 방의 참여자다 — 넣고 싶으면 받는 사람으로 같이 고르면 된다.
 */
export function NewChatDialog({
  open,
  onOpenChange,
  contacts,
  pending = false,
  onStart,
}: NewChatDialogProps) {
  const [selected, setSelected] = useState<Participant[]>([]);
  const [query, setQuery] = useState("");

  const selectedAddresses = useMemo(
    () => new Set(selected.map((person) => person.address)),
    [selected],
  );

  /**
   * 아직 칩으로 굳지 않았지만 주소 형태는 갖춘 입력.
   *
   * 주소를 다 쳐놓고 Enter를 누르지 않은 채 단추를 누르는 일이 잦다. 그걸 조용히
   * 버리면 받는 사람 없이 넘어가거나, 앞서 고른 사람에게만 열린다 — 어느 쪽이든
   * 사용자는 자기가 친 주소가 들어간 줄 안다.
   */
  const typedAddress = useMemo(() => {
    const address = normalizeAddress(query);
    if (!EMAIL_SHAPE.test(address)) return null;
    return selectedAddresses.has(address) ? null : address;
  }, [query, selectedAddresses]);

  const suggestions = useMemo(() => {
    const keyword = query.trim().toLowerCase();

    return contacts.filter((contact) => {
      if (selectedAddresses.has(contact.address)) return false;
      if (keyword.length === 0) return true;

      return (
        contact.displayName.toLowerCase().includes(keyword) ||
        contact.address.includes(keyword)
      );
    });
  }, [contacts, query, selectedAddresses]);

  function add(person: Participant) {
    if (selected.length >= MAX_RECIPIENTS) return;
    if (selectedAddresses.has(person.address)) return;

    setSelected((current) => [...current, person]);
    setQuery("");
  }

  /** 주소록에 없는 주소를 직접 쳐서 넣는 경우 */
  function addTypedAddress() {
    const address = normalizeAddress(query);
    if (!EMAIL_SHAPE.test(address)) return;

    add({
      id: address,
      address,
      displayName: address,
      initials: initialsFor(address),
      colorIndex: colorIndexFor(address),
      avatarUrl: null,
      status: "idle",
      blocked: false,
      isSelf: false,
      // 직접 친 주소다. 이 서버 사람인지는 서버만 알고, 보낼 때 거기서 갈린다.
      internal: false,
      presence: null,
    });
  }

  function remove(address: string) {
    setSelected((current) =>
      current.filter((person) => person.address !== address),
    );
  }

  function handleKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.nativeEvent.isComposing) return;

    if (event.key === "Enter") {
      event.preventDefault();
      const first = suggestions[0];
      if (first) add(first);
      else addTypedAddress();
      return;
    }

    // 빈 입력에서 백스페이스를 누르면 마지막 칩을 지운다
    if (event.key === "Backspace" && query.length === 0) {
      setSelected((current) => current.slice(0, -1));
    }
  }

  function reset() {
    setSelected([]);
    setQuery("");
  }

  function handleStart() {
    if (pending) return;

    // 치다 만 주소부터 칩으로 굳힌다. 아래 disabled 조건과 짝이다.
    const typed = typedAddress;
    const to = [...selected.map((person) => person.address), ...(typed ? [typed] : [])];
    if (to.length === 0) return;

    onStart({ to });
  }

  // 창이 닫히면 고르던 것을 지운다. 다음에 열었을 때 남의 선택이 남아 있으면 안 된다.
  function handleOpenChange(next: boolean) {
    if (!next) reset();
    onOpenChange(next);
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <Dialog.Content size="md">
        <div className="border-line-soft flex flex-col gap-4 border-b px-6.5 pt-6 pb-4.5">
          <Dialog.Title>새 채팅</Dialog.Title>
          <Dialog.Close />

          <div className="bg-surface-3 focus-within:outline-ink flex min-h-12 flex-wrap items-center gap-2 rounded-xl px-4 py-2 focus-within:outline-2 focus-within:outline-offset-2">
            {selected.map((person) => (
              <span
                key={person.address}
                className="bg-ink text-paper flex shrink-0 items-center gap-1.5 rounded-2xl py-1.25 pr-2 pl-2.5 text-[13px]"
              >
                {person.displayName}
                <button
                  type="button"
                  aria-label={`${person.displayName} 빼기`}
                  onClick={() => remove(person.address)}
                  className={`rounded opacity-60 hover:opacity-100 active:opacity-80 ${PRESS_FLAT} ${FOCUS_ON_DARK}`}
                >
                  <X className="size-3" />
                </button>
              </span>
            ))}

            <label htmlFor="new-chat-query" className="sr-only">
              이름 또는 메일 주소
            </label>
            <input
              id="new-chat-query"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={selected.length === 0 ? "이름 또는 메일 주소" : ""}
              className="text-ink placeholder:text-muted-3 min-w-40 flex-1 bg-transparent text-[15px] outline-none"
            />
          </div>
        </div>

        <div className="min-h-40 flex-1 overflow-y-auto pt-3 pb-2">
          <p className="text-muted-2 px-6.5 pt-1.5 pb-2.5 text-xs font-semibold">
            {query ? "검색 결과" : "최근 대화한 사람"}
          </p>

          {suggestions.length === 0 ? (
            <p className="text-muted-2 px-6.5 py-4 text-sm">
              {EMAIL_SHAPE.test(normalizeAddress(query))
                ? "Enter를 눌러 이 주소를 넣으세요"
                : "찾는 사람이 없어요"}
            </p>
          ) : (
            suggestions.map((contact) => (
              <button
                key={contact.address}
                type="button"
                onClick={() => add(contact)}
                className={`hover:bg-paper/70 active:bg-chip flex w-full items-center gap-3 px-6.5 py-2.5 text-left focus-visible:outline-offset-[-2px] ${PRESS_FLAT} ${FOCUS}`}
              >
                <Avatar
                  size="lg"
                  initials={contact.initials}
                  colorIndex={contact.colorIndex}
                  imageUrl={contact.avatarUrl}
                  label={contact.displayName}
                />
                <span className="flex min-w-0 flex-col gap-0.5">
                  <span className="text-ink text-[15px]">
                    {contact.displayName}
                  </span>
                  <span className="text-muted-2 line-clamp-1-ko text-xs">
                    {contact.address}
                  </span>
                </span>
              </button>
            ))
          )}
        </div>

        <div className="border-line-soft flex flex-col gap-2.5 border-t px-6.5 pt-4 pb-5.5">
          {/*
            방만 열린다는 것을 미리 말한다. 채팅 앱의 "대화 시작"은 상대에게
            알림이 가는 일이라, 아무 말도 안 나간다는 것을 모르면 고르기 전에
            망설이게 된다.
          */}
          <p className="text-muted-2 text-xs">
            대화창만 열려요. 상대는 첫 메일을 보내야 알 수 있어요.
          </p>
          <Button
            block
            onClick={handleStart}
            disabled={
              pending || (selected.length === 0 && typedAddress === null)
            }
          >
            {pending ? "여는 중…" : "대화 시작"}
          </Button>
        </div>
      </Dialog.Content>
    </Dialog>
  );
}
